import React, { useEffect, useMemo, useState } from 'react';
import { Bell, BellRing } from 'lucide-react';
import {
  getScheduleForDay,
  getCurrentProgram,
  getLanguageFlag,
  getProgramTypeIcon,
} from '../../utils/scheduleUtils';
import { useT } from '../i18n';
import { useKiooAudio } from '../AudioProvider';
import { isFavourited, toggleFavourite } from '../ProgramReminders';

const DAY_KEYS = [
  { key: 'Monday', short: 'schedule.mon' },
  { key: 'Tuesday', short: 'schedule.tue' },
  { key: 'Wednesday', short: 'schedule.wed' },
  { key: 'Thursday', short: 'schedule.thu' },
  { key: 'Friday', short: 'schedule.fri' },
  { key: 'Saturday', short: 'schedule.sat' },
  { key: 'Sunday', short: 'schedule.sun' },
];

const todayName = () => {
  const map = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return map[new Date().getDay()];
};

const MobileSchedule = () => {
  const t = useT();
  const { currentStation } = useKiooAudio();
  const [day, setDay] = useState(todayName());
  const [nowInfo, setNowInfo] = useState(() => getCurrentProgram());
  const [favTick, setFavTick] = useState(0);

  const bump = () => setFavTick((n) => n + 1);

  useEffect(() => {
    const id = setInterval(() => setNowInfo(getCurrentProgram()), 30_000);
    return () => clearInterval(id);
  }, []);

  const programs = useMemo(() => {
    try {
      return getScheduleForDay(day) || [];
    } catch (_e) {
      return [];
    }
  }, [day]);

  const currentProgramName =
    day === todayName() && nowInfo?.current
      ? (nowInfo.current.Program || nowInfo.current.name)
      : null;
  const currentStart =
    day === todayName() && nowInfo?.current
      ? (nowInfo.current['Start Time'] || nowInfo.current.startTime)
      : null;

  // For VOX we don't have a full weekly grid, but two recurring slots are
  // publicly known (Vox Talk Mon+Thu, Truth For Life Sat 1PM). Render them
  // as a partial "Known Weekly Broadcasts" card + a link to the station's
  // Facebook page for live announcements. Everything else still says
  // "Coming Soon". For Kilikezu we keep the pure Coming Soon placeholder.
  if (currentStation && currentStation.slug !== 'kioo') {
    const isVox = currentStation.slug === 'vox';
    return (
      <div className="px-4 pt-4 pb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">
          {t('schedule.title')}
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">
          {currentStation.name}
        </p>

        {isVox ? (
          <div className="mt-4 space-y-3" data-testid="mobile-schedule-vox">
            <div
              className="rounded-2xl bg-white border-2 p-4 shadow-sm"
              style={{ borderColor: 'var(--kioo-primary)' }}
            >
              <div
                className="text-[11px] font-extrabold uppercase tracking-wider"
                style={{ color: 'var(--kioo-primary)' }}
              >
                Known Weekly Broadcasts
              </div>
              <p className="mt-1 text-[11px] text-gray-500">
                A full grid isn't published yet. These recurring segments are
                confirmed via VOX Radio's official channels.
              </p>
              <ul className="mt-3 space-y-2">
                <li
                  data-testid="vox-schedule-vox-talk"
                  className="rounded-xl border border-gray-100 p-3 flex items-start gap-3"
                >
                  <div className="text-2xl leading-none">🎙</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 leading-tight">
                      Vox Talk
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Mondays &amp; Thursdays · Community &amp; interviews
                    </div>
                  </div>
                </li>
                <li
                  data-testid="vox-schedule-tfl"
                  className="rounded-xl border border-gray-100 p-3 flex items-start gap-3"
                >
                  <div className="text-2xl leading-none">📖</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 leading-tight">
                      Truth For Life
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Saturdays · 1:00 PM · Bible teaching
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <a
              href="https://www.facebook.com/voxradioliberia/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="vox-schedule-fb-link"
              className="flex items-center gap-3 rounded-2xl bg-[#1877F2] text-white p-3 shadow-sm active:scale-[0.98] transition-transform"
            >
              <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0 font-extrabold text-lg">
                f
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm leading-tight">
                  Live schedule updates on Facebook
                </div>
                <div className="text-[11px] opacity-90">
                  facebook.com/voxradioliberia
                </div>
              </div>
            </a>
          </div>
        ) : (
          <div
            data-testid="mobile-schedule-coming-soon"
            className="mt-6 rounded-2xl bg-white border-2 p-6 text-center shadow-sm"
            style={{ borderColor: 'var(--kioo-primary)' }}
          >
            <div className="text-4xl">📻</div>
            <div
              className="mt-2 text-2xl font-extrabold"
              style={{ color: 'var(--kioo-primary-dark)' }}
            >
              Coming Soon!
            </div>
            <p className="mt-2 text-sm text-gray-600">
              The full weekly schedule for {currentStation.name} will be
              published here.
            </p>
            {currentStation.relay_note && (
              <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
                🔁 {currentStation.relay_note}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 pt-4">
      <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">
        {t('schedule.title')}
      </h1>
      <p className="text-xs text-gray-500 mt-0.5">{t('schedule.subtitle')}</p>

      {/* Day tabs */}
      <div
        data-testid="mobile-schedule-day-tabs"
        className="mt-4 -mx-4 px-4 overflow-x-auto"
      >
        <div className="inline-flex gap-2 pb-2">
          {DAY_KEYS.map(({ key, short }) => {
            const isToday = key === todayName();
            const active = day === key;
            return (
              <button
                key={key}
                data-testid={`mobile-schedule-day-${key.toLowerCase()}`}
                onClick={() => setDay(key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition ${
                  active
                    ? 'bg-emerald-700 text-white border-emerald-700'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'
                }`}
              >
                {t(short)}
                {isToday && (
                  <span
                    className={`ml-1.5 text-[10px] font-bold ${
                      active ? 'text-emerald-100' : 'text-emerald-600'
                    }`}
                  >
                    · {t('schedule.today')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Program list */}
      <ul className="mt-3 space-y-2" data-testid="mobile-schedule-list">
        {programs.length === 0 && (
          <li className="text-sm text-gray-500 py-6 text-center">
            No programs scheduled.
          </li>
        )}
        {programs.map((p, idx) => {
          const name = p.Program;
          const start = p['Start Time'];
          const end = p['End Time'];
          const lang = p.Language;
          const type = p.Type;
          const isNow =
            currentProgramName === name && currentStart === start;
          return (
            <li
              key={`${start}-${idx}`}
              data-testid={`mobile-schedule-item-${idx}`}
              className={`rounded-2xl border p-3 flex items-start gap-3 ${
                isNow
                  ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-300'
                  : 'bg-white border-gray-100'
              }`}
            >
              <div className="flex-shrink-0 text-center min-w-[54px]">
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">
                  {start}
                </div>
                <div className="text-[10px] text-gray-400">to</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wide">
                  {end}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 leading-tight break-words">
                  {getProgramTypeIcon(type)} {name}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {getLanguageFlag(lang)} {lang} · {type}
                </div>
                {isNow && (
                  <div
                    data-testid="mobile-schedule-on-air-badge"
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-extrabold bg-emerald-700 text-white px-2 py-0.5 rounded-full"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                    {t('schedule.onAirNow')}
                  </div>
                )}
              </div>
              {(() => {
                const favSeed = favTick; // eslint-disable-line no-unused-vars
                const fav = {
                  station_slug: currentStation?.slug || 'kioo',
                  program_name: name,
                  day,
                  start_time: start,
                };
                const on = isFavourited(fav);
                return (
                  <button
                    data-testid={`mobile-schedule-fav-${idx}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await toggleFavourite(fav);
                      bump();
                    }}
                    aria-label={on ? 'Turn off reminder' : 'Remind me'}
                    className={`flex-shrink-0 self-center w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      on
                        ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300'
                        : 'bg-gray-100 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                    title={on ? 'Reminder on (5 min before)' : 'Remind me 5 min before'}
                  >
                    {on ? (
                      <BellRing className="w-4 h-4 fill-current" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                  </button>
                );
              })()}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MobileSchedule;
